import axios from "axios";
import ensureTrailingSlash from "../../../utils/url/url";

const getHeaders = tokens => {
  const headers = {
    Authorization: `Bearer ${tokens.accessToken}`,
    "content-type": "application/json",
  };

  if (tokens.idToken) {
    headers.idtoken = tokens.idToken;
  }
  return headers;
};

const getConfig = tokens => {
  const headers = getHeaders(tokens);
  return {
    headers,
  };
};

const createError = (status, errorMessage) => ({
  status,
  errorMessage,
});

const handleError = error => {
  if (error.response) {
    // server responded with error
    return Promise.reject(
      createError(error.response.status, error.response.statusText)
    );
  }
  if (error.request) {
    // The request was made but no response was received
    return Promise.reject(createError(520, error.message));
  }
  // Something happened in setting up the request that triggered an Error
  return Promise.reject(createError(520, error.message));
};

/**
 * Validates a URL to prevent Server-Side Request Forgery (SSRF) attacks.
 * Checks for:
 * - Path traversal attempts (../)
 * - Allowed hostnames (whitelist approach)
 * - Valid protocols (http/https only)
 *
 * @param {string} inputUrl - The URL to validate
 * @returns {string} The validated URL
 * @throws {Error} If validation fails
 */

const validateUrl = inputUrl => {
  try {
    // Minimal path validation
    if (inputUrl.includes("/../") || /\/%2e%2e\//i.test(inputUrl)) {
      throw new Error("Invalid path");
    }

    const url = new URL(inputUrl);

    // Protocol + host checks
    const allowedDomains = [
      new URL(process.env.REACT_APP_ORIGO_URL).hostname,
      new URL(process.env.REACT_APP_COMPENSATION_SERVICE).hostname,
    ]; // add your allowed domains here
    if (!allowedDomains.includes(url.hostname)) {
      throw new Error("Invalid host");
    }
    if (!["http:", "https:"].includes(url.protocol)) {
      throw new Error("Invalid protocol");
    }

    return url.href;
  } catch (error) {
    throw new Error(error.message);
  }
};

const RestService = {
  getToken: sessionId => {
    const authUrl = ensureTrailingSlash(process.env.REACT_APP_ORIGO_URL);
    const tokenApi = process.env.REACT_APP_ORIGO_TOKEN_API;
    const path = `${authUrl}${tokenApi}`;

    return axios
      .get(path, {
        headers: {
          Authorization: `OrigoSession ${sessionId}`,
        },
      })
      .then(response => Promise.resolve(response.data))
      .catch(error => handleError(error));
  },

  get: (tokens, path) => {
    try {
      const validatedUrl = validateUrl(path);
      // make the call
      return axios
        .get(validatedUrl, getConfig(tokens))
        .then(response =>
          // resolve with the result
          Promise.resolve(response.data)
        )
        .catch(error => handleError(error));
    } catch (error) {
      return Promise.reject(error);
    }
  },
  post: (tokens, path, data) => {
    try {
      const validatedUrl = validateUrl(path);
      // make the call
      return axios
        .post(validatedUrl, data, getConfig(tokens))
        .then(response =>
          // resolve with the result
          Promise.resolve(response.data)
        )
        .catch(error => handleError(error));
    } catch (error) {
      return Promise.reject(error);
    }
  },
  put: (tokens, path, data) => {
    try {
      const validatedUrl = validateUrl(path);
      // make the call
      return axios
        .put(validatedUrl, data, getConfig(tokens))
        .then(response =>
          // resolve with the result
          Promise.resolve(response.data)
        )
        .catch(error => handleError(error));
    } catch (error) {
      return Promise.reject(error);
    }
  },
  patch: (tokens, path, data) => {
    try {
      const validatedUrl = validateUrl(path);
      // make the call
      return axios
        .patch(validatedUrl, data, getConfig(tokens))
        .then(response =>
          // resolve with the result
          Promise.resolve(response.data)
        )
        .catch(error => handleError(error));
    } catch (error) {
      return Promise.reject(error);
    }
  },
  delete: (tokens, path, data) => {
    try {
      const validatedUrl = validateUrl(path);
      // make the call
      return axios
        .delete(validatedUrl, data, getConfig(tokens))
        .then(response =>
          // resolve with the result
          Promise.resolve(response.data)
        )
        .catch(error => handleError(error));
    } catch (error) {
      return Promise.reject(error);
    }
  },
};

export default RestService;
